import {
  type ContainerStatusEntity,
  extractErrorMessage,
  startOfDay,
  timeZone,
  useGetDispatchQuery,
  useGetTtaAuditSummariesQuery,
  useSearchContainerLevelsQuery,
  useSearchTtasQuery,
} from "@ots-frontend/ots-data";
import { skipToken } from "@reduxjs/toolkit/query";
import { useMemo, useState } from "react";
import { ConnectedTicketAuditDialog } from "../../audit/connected/ConnectedTicketAudit";
import { ActivityAttachmentsDialog } from "../../driver-activity/ActivityAttachmentsDialog";
import type { ActivityEntry } from "../../driver-activity/model/activity-types";
import type { OtsBrand, OtsColorSchemePreference } from "../../theme/OtsTheme";
import {
  type ContainerDetailsRange,
  containerDetailsRangeMillis,
  containerTicketId,
  type ContainerTicketParty,
  toContainerTicketEntries,
} from "../container-details-model";
import { ContainerDetailsPanel } from "../ContainerDetailsPanel";

export type ConnectedContainerDetailsPanelProps = {
  /** The grid's row for the container; refreshed with the grid. */
  container: ContainerStatusEntity;
  /** Shows the stale-poll clock among the indicators, as the grid's display options decide. */
  stalePoll?: boolean;
  /** Whether ticket rows name the hauler (HubGo) or the driver (DispatchGo). */
  ticketParty?: ContainerTicketParty;
  /** Opens a ticket's profile; the app owns the route. */
  onOpenTicket?: (tta: { id: string; database?: string }) => void;
  onClose: () => void;
  className?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

const MINUTE = 60_000;

/** The container details panel on the ticket search, audit summaries, SCADA history, and the latest dispatch. */
export const ConnectedContainerDetailsPanel = ({ container, stalePoll, ticketParty, onOpenTicket, onClose, className, brand, colorScheme }: ConnectedContainerDetailsPanelProps) => {
  const [range, setRange] = useState<ContainerDetailsRange>("24h");
  // Whole minutes keep the query arguments, and so the cache, steady between renders.
  const [now, setNow] = useState(() => Math.floor(Date.now() / MINUTE) * MINUTE);
  const changeRange = (next: ContainerDetailsRange) => {
    setRange(next);
    setNow(Math.floor(Date.now() / MINUTE) * MINUTE);
  };
  const span = { containerId: container.containerId, fromDttm: now - containerDetailsRangeMillis[range], toDttm: now };

  const ttas = useSearchTtasQuery(span);
  const auditTargets = useMemo(() => (ttas.currentData ?? []).map((tta) => ({ objectId: tta.id, tenantCode: tta.database })), [ttas.currentData]);
  const audits = useGetTtaAuditSummariesQuery(auditTargets.length ? { targets: auditTargets } : skipToken);
  const levels = useSearchContainerLevelsQuery(span);
  const dispatch = useGetDispatchQuery(container.latestDispatchId ?? skipToken);
  // currentData, not data: a new range shows its loader rather than the last range's rows.
  const entries = useMemo(() => toContainerTicketEntries(ttas.currentData, audits.data), [ttas.currentData, audits.data]);

  const [auditEntry, setAuditEntry] = useState<ActivityEntry>();
  const [attachments, setAttachments] = useState<{ entry: ActivityEntry; index: number }>();

  return (
    <>
      <ContainerDetailsPanel
        className={className}
        containerId={container.containerId}
        containerName={container.containerName}
        siteName={[container.siteName, container.siteCompanyName].filter(Boolean).join(" · ") || undefined}
        status={container}
        stalePoll={stalePoll}
        range={range}
        onRangeChange={changeRange}
        onClose={onClose}
        tickets={{
          data: entries,
          isLoading: ttas.isFetching && !ttas.currentData,
          error: ttas.error && extractErrorMessage(ttas.error, "Please try again."),
          onRetry: () => void ttas.refetch(),
        }}
        levels={{
          data: levels.currentData ?? [],
          isLoading: levels.isFetching && !levels.currentData,
          error: levels.error && extractErrorMessage(levels.error, "Please try again."),
          onRetry: () => void levels.refetch(),
        }}
        dispatch={dispatch.data}
        dayStart={startOfDay(now).getTime()}
        timeZone={timeZone.value}
        ticketParty={ticketParty}
        onOpenTicket={onOpenTicket && ((entry) => entry.tta && onOpenTicket(entry.tta))}
        onOpenAudit={(entry) => setAuditEntry(entry)}
        onOpenAttachments={(entry) => setAttachments({ entry, index: 0 })}/>
      <ConnectedTicketAuditDialog
        open={!!auditEntry?.tta}
        onClose={() => setAuditEntry(undefined)}
        subtitle={auditEntry && [containerTicketId(auditEntry), auditEntry.pickup?.personName].filter(Boolean).join(" · ")}
        ttaId={auditEntry?.tta?.id ?? ""}
        tenantCode={auditEntry?.tta?.database}
        warnings={auditEntry?.warnings}
        allowRun
        brand={brand}
        colorScheme={colorScheme}/>
      <ActivityAttachmentsDialog
        entry={attachments?.entry}
        index={attachments?.index ?? 0}
        onIndexChange={(index) => setAttachments((current) => current && { ...current, index })}
        onClose={() => setAttachments(undefined)}
        timeZone={timeZone.value}
        brand={brand}
        colorScheme={colorScheme}/>
    </>
  );
};
